import { useAuth } from '@/contexts/AuthContext'
import type { Recipe } from '@/types'

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams} from 'react-router-dom'

import { Plus, ShoppingCart, ArrowLeft, Heart, Trash2 } from 'lucide-react'

import { MixLayout } from "@/components/layout"
import { Button, Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription, ConnectionBanner, ScheduleView } from '@/components/ui'
import { useWebSocketContext } from '@/contexts'
import { useKeydownShortcut } from '@/hooks'
import { useNavigationContext } from '@/contexts/NavigationContext'

/**
 * Per-recipe favorite button with animation feedback.
 * Triggers a dramatic pop when the current user favorites,
 * and a gentler pulse when another contributor's favorite arrives via Realtime.
 */
function RecipeFavorite({
  recipe,
  mixId,
  userId,
  favoriteRecipe,
}: {
  recipe: Recipe
  mixId: string
  userId: string | undefined
  favoriteRecipe: (mixId: string, recipeId: string, action: 'add' | 'remove') => void
}) {
  const heartRef = useRef<HTMLButtonElement>(null)
  const namesRef = useRef<HTMLSpanElement>(null)
  const pendingRef = useRef(false)

  const prevFavorites = useRef<Recipe['favorites']>(recipe.favorites)
  const prevIsFavorited = useRef<boolean>(false)

  const isFavorited = recipe.favorites?.some((f) => f.userId === userId) ?? false
  const favCount = recipe.favorites?.length ?? 0
  const prevFavCount = prevFavorites.current?.length ?? 0

  useEffect(() => {
    const wasFavorited = prevIsFavorited.current
    const wasCount = prevFavCount

    if (isFavorited && !wasFavorited && heartRef.current) {
      // Current user just favorited — dramatic pop
      heartRef.current.classList.add('animate-heart-pop')
      heartRef.current.addEventListener(
        'animationend',
        () => heartRef.current?.classList.remove('animate-heart-pop'),
        { once: true }
      )
    } else if (favCount > wasCount && namesRef.current) {
      // Another user favorited — gentle pulse on names or heart
      namesRef.current.classList.add('animate-heart-pulse')
      namesRef.current.addEventListener(
        'animationend',
        () => namesRef.current?.classList.remove('animate-heart-pulse'),
        { once: true }
      )
    }

    prevIsFavorited.current = isFavorited
    prevFavorites.current = recipe.favorites
  }, [recipe.favorites, isFavorited, favCount, prevFavCount])

  const handleToggle = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (pendingRef.current) return
    pendingRef.current = true
    try {
      await favoriteRecipe(mixId, recipe.id, isFavorited ? 'remove' : 'add')
    } finally {
      pendingRef.current = false
    }
  }

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button
        ref={heartRef}
        type="button"
        onClick={handleToggle}
        className={`inline-flex items-center transition-colors ${isFavorited
          ? 'text-red-500'
          : 'text-muted-foreground hover:text-red-500'
        }`}
        aria-label={isFavorited ? 'Remove favorite' : 'Add favorite'}
      >
        <Heart
          className="h-4 w-4"
          fill={isFavorited ? 'currentColor' : 'none'}
        />
      </button>
      {favCount > 0 && (
        <span ref={namesRef} className="text-xs text-muted-foreground">
          {recipe.favorites!.map((f) => f.userName).join(', ')}
        </span>
      )}
    </div>
  )
}

export default function MixPage() {
  const { mixId = '' } = useParams()
  const [currentIndex, setCurrentIndex] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; recipe: Recipe | null }>({ open: false, recipe: null })
  const { getRecipes, isMix, loading, mixes, favoriteRecipe, deleteRecipe } = useWebSocketContext()
  const { user } = useAuth()
  const { navigateTab } = useNavigationContext()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !isMix(mixId)) {
      navigate('/home')
    }
  }, [])

  const handleGroceryListClick = () => {
    navigateTab("grocerylist")
  }

  const handleMouseOver = (index: number) => {
    if (currentIndex != index) {
      setCurrentIndex(index)
    }
  }

  const handleMouseLeave = () => {
    setCurrentIndex(null)
  }

  const mix = mixes.find(m => m.id === mixId)
  const recipes = getRecipes(mixId)
  const isContributor = user?.id && mix ? (mix.ownerId === user.id || mix.contributors.includes(user.id)) : false

  // Keyboard shortcuts only apply to standard mixes; still register unconditionally
  // to comply with React Rules of Hooks (hook count must be stable across renders).
  useKeydownShortcut(
    { key: 'j', ctrl: false, meta: false, alt: false },
    () => {
      if (typeof currentIndex === 'number' && currentIndex < recipes.length - 1) {
        setCurrentIndex(currentIndex + 1)
      }
    },
    'Focus next recipe',
    'Focus on next recipe'
  );

  useKeydownShortcut(
    { key: 'k', ctrl: false, meta: false, alt: false },
    () => {
      if (typeof currentIndex === 'number' && currentIndex !== 0) {
        setCurrentIndex(currentIndex - 1)
      }
    },
    'Focus next recipe',
    'Focus on next recipe'
  );

  if (loading || !mix) return null

  // Schedule mixes render the day/slot grid view
  if (mix.type === 'schedule') {
    return <ScheduleView mixId={mixId} />
  }

  return (

      <MixLayout>
        <div className="sticky top-0 bg-background z-30">
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => navigate('/home')}
              className="h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer flex-shrink-0"
              aria-label="Back to mixes"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-semibold text-foreground truncate">{mix!.name}</h2>
              <p className="text-xs text-muted-foreground">Recipes</p>
            </div>
            <button
              onClick={() => navigate(`/mixes/${mixId}/add-recipes`)}
              className={`h-10 w-10 flex items-center justify-center rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted cursor-pointer flex-shrink-0`}
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
          <ConnectionBanner />
        </div>

        <div className="flex flex-1 justify-center">
          <div className="flex flex-col w-full min-h-0">
            <div className="flex-1 overflow-y-auto pb-24">
              {recipes.length === 0 ? (
                <div className="flex flex-col gap-4 px-10 h-full items-center justify-center">
                  <div className="rounded-xl border border-border bg-card p-6 text-center w-full">
                    <p className="text-muted-foreground">
                      No recipes yet. <span className="font-semibold text-foreground">Click the + icon</span> to add a recipe!
                    </p>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-6 text-center w-full">
                    <p className="text-muted-foreground">
                      {mix.ingredients.length === 0
                        ? 'No ingredients yet. Add ingredients directly on the grocery list!'
                        : <><span className="font-semibold text-foreground">{mix.ingredients.length} total ingredient{mix.ingredients.length === 1 ? '' : 's'}</span> in this mix.</>}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 py-4 mx-4" onMouseLeave={handleMouseLeave}>
                  {recipes.map((recipe: Recipe, index: number) => (
                    <Card
                      className={`relative group rounded-lg border transition-all duration-200 hover-supported:hover:shadow-md hover-supported:hover:scale-105 hover-supported:hover:z-10 overflow-hidden border-border hover-supported:hover:border-primary/50 ${currentIndex === index ? 'hover-supported:border-primary/50' : ''}`}
                      key={recipe.id}
                      onMouseOver={() => handleMouseOver(index)}
                      onClick={() => navigate(`/mixes/${mixId}/recipes/${recipe.id}`)}
                    >
                      {/* Image */}
                      <div
                        className="h-32 w-full bg-muted overflow-hidden"
                      >
                        {recipe.image ? (
                          <img
                            src={recipe.image}
                            alt={recipe.name}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement
                              target.style.display = 'none'
                              target.parentElement!.innerHTML = '<div class="h-full w-full flex items-center justify-center"><span class="text-muted-foreground text-sm">🍳 Recipe</span></div>'
                            }}
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center">
                            <span className="text-muted-foreground text-sm">🍳 Recipe</span>
                          </div>
                        )}
                      </div>

                      {/* Heart + favorited-by names — animated */}
                      <div className="px-3 pt-2 flex items-center justify-between">
                        <RecipeFavorite
                          recipe={recipe}
                          mixId={mixId}
                          userId={user?.id}
                          favoriteRecipe={favoriteRecipe}
                        />
                        {isContributor && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              setConfirmDelete({ open: true, recipe })
                            }}
                            className="inline-flex items-center text-muted-foreground hover:text-red-500 transition-colors cursor-pointer"
                            aria-label={`Remove ${recipe.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>

                      {/* Title + author — click to navigate */}
                      <Link
                        to={`/mixes/${mixId}/recipes/${recipe.id}`}
                        className="block"
                      >
                        <CardHeader className="p-3">
                          <CardTitle className="text-base leading-tight truncate hover:underline">
                            {recipe.name}
                          </CardTitle>
                          <CardDescription>
                            Added by {recipe.sharerName || 'Unknown'}
                          </CardDescription>
                        </CardHeader>
                      </Link>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom sheet */}
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card p-4 shadow-lg text-foreground">
          <div className="w-full max-w-[768px] mx-auto flex items-center justify-between gap-4 px-4">
            <span className="text-sm font-medium">
              {recipes.length} recipes
            </span>
            <Button onClick={handleGroceryListClick}>
              <ShoppingCart className="size-4" />
              Grocery List →
            </Button>
          </div>
        </div>

        {/* Remove recipe confirmation dialog */}
        {confirmDelete.open && confirmDelete.recipe && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setConfirmDelete({ open: false, recipe: null })}>
            <div className="mx-4 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
              <Card>
                <CardHeader className="text-center">
                  <CardTitle>Remove Recipe</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground text-center">
                    Are you sure you want to remove <span className="font-medium text-foreground">{confirmDelete.recipe.name}</span> from this mix?
                  </p>
                </CardContent>
                <CardFooter className="gap-2">
                  <Button variant="outline" onClick={() => setConfirmDelete({ open: false, recipe: null })} className="flex-1">
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => {
                      if (confirmDelete.recipe) {
                        deleteRecipe(mixId, confirmDelete.recipe.id)
                      }
                      setConfirmDelete({ open: false, recipe: null })
                    }}
                    className="flex-1"
                  >
                    Remove
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        )}
      </MixLayout>

  )
}
